// TS mirror of the backend `app/services/llm/functions/actions.py::A` action types.
// SAM tools publish `sam:action` events; the dispatcher (useSamActions) maps these to
// the dynamic workspace. Mapping is by suffix/keyword so new modules need no new branch.

export type SamModule =
  | 'tasks'
  | 'notes'
  | 'properties'
  | 'meetings'
  | 'transactions'
  | 'proposals'
  | 'jobs'
  | 'calendar'
  | 'billing'
  | 'notifications'
  | 'users'
  | 'roles'
  | 'communications'
  | 'my_todos'
  | 'workflow'
  | 'engagements'
  // PCR-FEAT-02 / 03 slices — each has a workspace panel and (planned) detail cards.
  // The backend already publishes OPEN_/REFRESH_ actions with these module values.
  | 'contacts'
  | 'warranties'
  | 'measurements'
  | 'media';

/** SAM-authored communication draft carried by a DRAFT_COMMUNICATION action. The user reviews and
 *  sends it from the ComposeCard — SAM never sends directly. */
export interface SamCommunicationDraft {
  channel: 'email' | 'sms' | 'in_app';
  recipients: Array<{
    recipient_type: string;
    user_id?: string;
    contact_id?: string;
    name?: string;
    email?: string;
    phone?: string;
  }>;
  subject: string;
  body: string;
  context: { context_type: string; entity_id: string; display_label?: string | null } | null;
}

/** A backend-computed filter chip shown above a card list (e.g. "Open (2)"). */
export interface SamFacet {
  key: string;
  label: string;
  count: number;
  /** kwargs the backend re-runs the list tool with when this chip is clicked. */
  args: Record<string, unknown>;
}

/** One action item inside a `sam:action` envelope. */
export interface SamAction {
  action_type: string;
  module?: SamModule;
  filter?: Record<string, unknown>;
  prefill?: Record<string, unknown>;
  record_id?: string;
  /** Labels for the workspace "⋯" recent-views entry ONLY — never the card header or an inline
   *  chat card. A write that lands the user on a surface (create_task / create_meeting) uses these
   *  to name its menu entry after the record it just created, without gaining a chat card the way
   *  passing `title`/`caption` would. See `publish_action(view_title=…)`. */
  view_title?: string;
  view_caption?: string;
  /** REPLAY_RECENT_VIEW only — the stored entry's `context_ref.type` (e.g. `workflow_settings`,
   *  `company_workflows`), for surfaces with no natural `record_id` where `contextRefFor(module,
   *  recordId)` can't reconstruct the type on its own. */
  context_ref_type?: string;
  /** 0-based card index for OPEN_CARD navigation (e.g. "open 1st task" → index 0). */
  index?: number;
  count?: number;
  records?: Record<string, unknown>[];
  /** Backend-computed filter chips for the card list. */
  facets?: SamFacet[];
  // persistent action-card fields (panel/list reads) — LLM-authored heading + summary.
  title?: string;
  caption?: string;
  // confirmation-card fields (action_type === 'confirmation')
  call_id?: string;
  tool_name?: string;
  tool_args?: Record<string, unknown>;
  // confirmation-result fields (action_type === 'confirmation_result') — reports what actually
  // happened after the user approved a guardrailed tool, keyed back to the card via call_id.
  status?: 'success' | 'failed' | 'cancelled';
  status_message?: string;
  // The tool's returned `data` dict (confirmation_result only) — e.g. create_engagement's
  // active_engagement_exists conflict payload.
  data?: Record<string, unknown> | null;
  // view-control fields (SWITCH_VIEW / NAVIGATE_CARDS)
  /** SWITCH_VIEW target surface. */
  target?: 'chat' | 'workspace';
  /** NAVIGATE_CARDS relative step; NAVIGATE_CALENDAR also uses 'today'. */
  direction?: string;
  /** NAVIGATE_CARDS absolute 1-based card number to jump to. */
  position?: number;
  /** NAVIGATE_CALENDAR: period unit 'day' | 'week' | 'month' */
  unit?: string;
  /** NAVIGATE_CALENDAR: switch calendar view 'day' | 'week' | 'month' */
  view?: string;
  /** NAVIGATE_CALENDAR: jump to specific YYYY-MM-DD date */
  date?: string;
  /** DRAFT_COMMUNICATION payload — a reviewable email/SMS/in-app draft. */
  draft?: SamCommunicationDraft;
  /** NAVIGATE_ROUTE frontend path to push (e.g. "/tasks/new", "/tasks/abc123"). */
  path?: string;
  /** SWITCH_TASK_TAB / SWITCH_MEETING_TAB target tab name. */
  tab?: string;
  /** OPEN_NOTE_TAKER optional business-context link for the new note. */
  context_type?: string;
  entity_id?: string;
  /** SET_NOTE_FOLLOWUP_INCLUDED — one or more suggestion_ids to check/uncheck; omit both to
   *  apply to every pending suggestion on the note (select all / unselect all). */
  suggestion_id?: string;
  suggestion_ids?: string[];
  /** SET_NOTE_FOLLOWUP_INCLUDED — the checked state to apply. */
  included?: boolean;
  /** EXPAND_NOTE_FOLLOWUP — optional field edits to apply while opening the follow-up for review. */
  fields?: Record<string, unknown>;
}

/** The `sam:action` socket payload — `actions[]` (CRM) or singular `action` (confirmation). */
export interface SamActionEnvelope {
  actions?: SamAction[];
  action?: SamAction;
  session_id?: string;
  message_id?: string;
  source?: string;
}

export const CONFIRMATION = 'confirmation';
/** Reports the real outcome of a guardrailed tool call after the user approved it — keyed back
 *  to the ConfirmCard via call_id so it can show the actual result instead of just the click. */
export const CONFIRMATION_RESULT = 'confirmation_result';
export const OPEN_CARD = 'OPEN_CARD';
export const CLOSE_CARD = 'CLOSE_CARD';
export const SWITCH_VIEW = 'SWITCH_VIEW';
export const NAVIGATE_CARDS = 'NAVIGATE_CARDS';
export const OPEN_NOTE_TAKER = 'OPEN_NOTE_TAKER';
export const STOP_NOTE_TAKER = 'STOP_NOTE_TAKER';
/** Trigger the "Create all selected" batch on the open note review card. */
export const EXECUTE_NOTE_FOLLOWUPS = 'EXECUTE_NOTE_FOLLOWUPS';
/** Check/uncheck one, several, or all follow-up suggestions on the open note review card. */
export const SET_NOTE_FOLLOWUP_INCLUDED = 'SET_NOTE_FOLLOWUP_INCLUDED';
/** Expand a follow-up suggestion's review/edit panel (no create) — mirrors clicking "Review". */
export const EXPAND_NOTE_FOLLOWUP = 'EXPAND_NOTE_FOLLOWUP';
export const DRAFT_COMMUNICATION = 'DRAFT_COMMUNICATION';
/** Navigate to a frontend route (e.g. /tasks/new or /tasks/:id). */
export const NAVIGATE_ROUTE = 'NAVIGATE_ROUTE';
/** Switch the active tab in the open task detail panel. */
export const SWITCH_TASK_TAB = 'SWITCH_TASK_TAB';
/** Switch the active tab in the open meeting detail panel. */
export const SWITCH_MEETING_TAB = 'SWITCH_MEETING_TAB';
/** Invalidate the RTK Query comments cache for a specific task. */
export const REFRESH_TASK_COMMENTS = 'REFRESH_TASK_COMMENTS';
/** Invalidate the RTK Query cache for a specific task detail (e.g. after update_task). */
export const REFRESH_TASK_DETAIL = 'REFRESH_TASK_DETAIL';
/** Invalidate the RTK Query cache for a specific meeting detail (after reschedule/cancel/add_meeting_note
 *  — those tools hit the backend directly via MeetingService, bypassing the frontend's own RTK
 *  mutations, so nothing else invalidates the open detail's cache). */
export const REFRESH_MEETING_DETAIL = 'REFRESH_MEETING_DETAIL';
/** Navigate the on-screen calendar (next/prev day/week/month, jump to date, switch view). */
export const NAVIGATE_CALENDAR = 'NAVIGATE_CALENDAR';
/** Open the single-engagement Workflow surface (get_engagement_workflow_state / create_engagement).
 *  Also reused (with module: 'workflow', no record_id) by list_pending_approvals for the
 *  approvals-only view — see OPEN_ENGAGEMENTS_PANEL below. */
export const OPEN_WORKFLOW_PANEL = 'OPEN_WORKFLOW_PANEL';
/** Re-fetch the open Workflow surface's state/transitions/approvals (after execute_workflow_transition
 *  / decide_workflow_approval — those hit the workflow service directly, bypassing the frontend's own
 *  RTK mutations, so nothing else invalidates the open surface's cache). */
export const REFRESH_WORKFLOW = 'REFRESH_WORKFLOW';
/** Open the Engagements list panel (search_engagement: module 'engagements'). Also reused by
 *  list_pending_approvals with module 'workflow' for the approvals-only Workflow surface. */
export const OPEN_ENGAGEMENTS_PANEL = 'OPEN_ENGAGEMENTS_PANEL';
/** Apply a filter to the open Engagements list panel. */
export const APPLY_ENGAGEMENTS_FILTER = 'APPLY_ENGAGEMENTS_FILTER';
/** Re-fetch the Engagements list panel. */
export const REFRESH_ENGAGEMENTS = 'REFRESH_ENGAGEMENTS';
/** Open the Properties list panel (search_engagement / create_property: module 'properties'). */
export const OPEN_PROPERTIES_PANEL = 'OPEN_PROPERTIES_PANEL';
/** Apply a filter to the open Properties list panel. */
export const APPLY_PROPERTIES_FILTER = 'APPLY_PROPERTIES_FILTER';
/** Re-fetch the Properties list panel (after create_property / update_property). */
export const REFRESH_PROPERTIES = 'REFRESH_PROPERTIES';
/** Invalidate RTK Query cache for one open property detail (record_id = property_id) — e.g. after
 *  execute_workflow_transition changes that property's active engagement's stage/status, so the
 *  panel reflects it without the user leaving and reopening the card. */
export const REFRESH_PROPERTY_DETAIL = 'REFRESH_PROPERTY_DETAIL';
/** Open the Workflow SETTINGS surface (get_workflow_customization) — module: 'workflow' (reused),
 *  but a DISTINCT surface from OPEN_WORKFLOW_PANEL: the tenant's whole workflow STRUCTURE (all
 *  stages -> processes -> statuses -> transitions), no engagement in the picture. Distinguished
 *  from OPEN_WORKFLOW_PANEL/OPEN_ENGAGEMENTS_PANEL by this literal action_type string, not just
 *  module + `_PANEL` suffix — see isWorkflowModuleAction's comment in useSamActions. */
export const OPEN_WORKFLOW_SETTINGS_PANEL = 'OPEN_WORKFLOW_SETTINGS_PANEL';
/** Re-fetch the open Workflow Settings surface (after toggle_workflow_item / rename_workflow_label /
 *  add_workflow_stage / add_workflow_process / add_workflow_status / add_workflow_transition /
 *  set_transition_authority — those hit the customization service directly, bypassing the
 *  frontend's own RTK mutations). */
export const REFRESH_WORKFLOW_SETTINGS = 'REFRESH_WORKFLOW_SETTINGS';
/** Open the Company Processes (workflow list) card — first step when tenant_admin asks to
 *  configure their company processes (list_company_workflows tool). After the user clicks
 *  "Customize", the WorkflowSettingsCard opens. Distinct from OPEN_WORKFLOW_SETTINGS_PANEL
 *  (which goes straight to the editor); this one shows the selectable overview first. */
export const OPEN_COMPANY_WORKFLOWS_PANEL = 'OPEN_COMPANY_WORKFLOWS_PANEL';

/** Reopen a workspace "⋯" recent-views entry (list_recent_views / open_recent_view tools).
 *  Module-agnostic on purpose: `module`/`filter`/`record_id`/`title`/`caption` on the action carry
 *  everything `useChatWorkspace.replayView` needs to dispatch to the right surface — the backend
 *  never re-derives a module-specific OPEN_*_PANEL/OPEN_*_DETAIL pair for the target. */
export const REPLAY_RECENT_VIEW = 'REPLAY_RECENT_VIEW';

/** PCR-FEAT-02 Contacts — published by search_contacts / create_contact / link_contact_to_property. */
export const OPEN_CONTACTS_PANEL = 'OPEN_CONTACTS_PANEL';
export const APPLY_CONTACTS_FILTER = 'APPLY_CONTACTS_FILTER';
export const REFRESH_CONTACTS = 'REFRESH_CONTACTS';
export const OPEN_CONTACT_DETAIL = 'OPEN_CONTACT_DETAIL';
export const REFRESH_CONTACT_DETAIL = 'REFRESH_CONTACT_DETAIL';
